import React from "react";
import { Grid, TextField, Box, MenuItem, Button } from "@mui/material";

export default function ApiRestStepForm({
  data,
  updateData,
  setIsValid,
  showErrors,
}) {
  const [jsonFeedback, setJsonFeedback] = React.useState(null);
  const [isJsonValid, setIsJsonValid] = React.useState(true);

  /* =====================================
     🔍 Validação JSON (body opcional)
  ===================================== */
  const validateJsonBody = (value) => {
    if (!value || !value.trim()) {
      return { valid: true, message: null }; // body opcional
    }

    try {
      const parsed = JSON.parse(value);

      if (typeof parsed !== "object" || parsed === null) {
        return {
          valid: false,
          message: "O JSON deve ser objeto ou array",
        };
      }

      return { valid: true, message: "JSON válido ✔" };
    } catch {
      return { valid: false, message: "JSON inválido" };
    }
  };

  /* =====================================
     🎨 Auto indentação
  ===================================== */
  const formatJson = () => {
    if (!data.body) return;

    try {
      const parsed = JSON.parse(data.body);
      const formatted = JSON.stringify(parsed, null, 2); // 2 espaços
      updateData({ body: formatted });
      setJsonFeedback("JSON formatado ✔");
      setIsJsonValid(true);
    } catch {
      // não formata se estiver inválido
    }
  };

  /* =====================================
     🧠 Erros básicos
  ===================================== */
  const errors = {};

  if (!data.title) errors.title = "Nome é obrigatório";
  if (!data.desc) errors.desc = "Descrição é obrigatória";
  if (!data.method) errors.method = "Método é obrigatório";
  if (!data.url) errors.url = "URL é obrigatória";
  if (!data.authorization) errors.authorization = "Authorization é obrigatório";

  if (data.method === "POST" && data.body) {
    const result = validateJsonBody(data.body);
    if (!result.valid) errors.body = result.message;
  }

  /* =====================================
     ✅ Validação geral
  ===================================== */
  React.useEffect(() => {
    const baseValid =
      data.title && data.desc && data.method && data.url && data.authorization;

    const bodyValid = data.method !== "POST" || !data.body || isJsonValid;

    setIsValid(baseValid && bodyValid);
  }, [data, isJsonValid, setIsValid]);

  /* =====================================
     🔄 Handle Change
  ===================================== */
  const handleChange = (name) => (e) => {
    const value = e.target.value;

    if (name === "method" && value === "GET") {
      updateData({ method: value, body: "" });
      setJsonFeedback(null);
      setIsJsonValid(true);
      return;
    }

    if (name === "body") {
      const result = validateJsonBody(value);
      setJsonFeedback(result.message);
      setIsJsonValid(result.valid);
    }

    updateData({ [name]: value });
  };

  /* =====================================
     🎨 UI
  ===================================== */
  return (
    <Box sx={{ width: "100%", maxWidth: 900 }}>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Nome da API"
            value={data.title || ""}
            onChange={handleChange("title")}
            error={showErrors && !!errors.title}
            helperText={showErrors && errors.title}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Descrição"
            multiline
            minRows={3}
            value={data.desc || ""}
            onChange={handleChange("desc")}
            error={showErrors && !!errors.desc}
            helperText={showErrors && errors.desc}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            label="Método HTTP"
            value={data.method || ""}
            onChange={handleChange("method")}
            error={showErrors && !!errors.method}
            helperText={showErrors && errors.method}
            sx={{
              "& .MuiOutlinedInput-root": {
                height: 56,
              },
              "& .MuiOutlinedInput-input": {
                padding: "16.5px 14px",
              },
            }}
          >
            <MenuItem value="GET">GET</MenuItem>
            <MenuItem value="POST">POST</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="URL da API"
            value={data.url || ""}
            onChange={handleChange("url")}
            error={showErrors && !!errors.url}
            helperText={showErrors && errors.url}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            fullWidth
            label="Authorization Token"
            value={data.authorization || ""}
            onChange={handleChange("authorization")}
            error={showErrors && !!errors.authorization}
            helperText={showErrors && errors.authorization}
          />
        </Grid>

        {data.method === "POST" && (
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Body (JSON) — Opcional"
              multiline
              minRows={6}
              value={data.body || ""}
              onChange={handleChange("body")}
              onBlur={formatJson} // 🔥 auto formata ao sair
              error={
                (showErrors && !!errors.body) || (!!data.body && !isJsonValid)
              }
              helperText={
                showErrors && errors.body ? errors.body : jsonFeedback
              }
              sx={{
                "& textarea": {
                  fontFamily: "monospace",
                },
              }}
            />
          </Grid>
        )}
      </Grid>
    </Box>
  );
}
